import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import { Card } from '../../Card';
import { Loader } from '../../Loader';
import { ErrorBoundary } from '../../../containers';
import { formatMessageHelper, FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import { injectIntl } from 'react-intl';
import { WrappedComponentProps as InjectedIntlProps } from 'react-intl/src/components/injectIntl';
import { addModifiers } from '../../../shared/helpers';
import { getColumnTransform } from '../../../containers/DataExplorer/DataTableCommon/DataTableCommon';
import { CellContext, Column, ColumnPinningState, flexRender, getCoreRowModel, Row, useReactTable } from '@tanstack/react-table';
import { AggregationFnOption } from '@tanstack/table-core/src/features/Grouping';
import { getHeaderClassName } from '../../Learn/LearnBlocks/BlockTable';
import { getFooterGroups } from '../../../shared/table/table-helpers';
import { ColumnDef } from '@tanstack/table-core/src/types';

import './AlphaPyramids.scss';
import { Popup } from 'semantic-ui-react';
import { getDatasetsPath } from '../../../containers/DataExplorer/dataExplorerRouterPaths';
import { DelayByRegion, PyramidMultiplier, useAlphaPyramidMultipliers } from '../../../shared/api/apiHooks';
import { useDefaultUniverses } from './AlphaPyramids';
import analytics from '../../../shared/services/analytics/analytics';

function PyramidMultipliersWithoutIntl({ intl }: InjectedIntlProps) {
  return (
    <>
      <div className="research-paradigm__header">
        <h2 className="genius__subtitle"> {formatMessageHelper(intl, 'genius.pyramidMultipliers.title')}</h2>
      </div>
      <Card hasHeader={false}>
        <ErrorBoundary isNotFullScreen={true}>
          <PyramidMultipliersTable intl={intl} />
        </ErrorBoundary>
      </Card>
    </>
  );
}

export const PyramidMultipliers = React.memo(injectIntl(PyramidMultipliersWithoutIntl));

export interface PyramidColumn<Data> {
  id: string;
  name: string;
  active?: boolean;
  cell?: (props: CellContext<Data, Data>) => JSX.Element;
  accessor?: string | ((data: Data) => any);
  accessorFn?: (data: Data) => any;
  readOnly?: boolean;
  enableSorting?: boolean;
  display?: boolean;
  type?: string;
  tab?: string[];
  size?: number;
  alignHeader?: 'left' | 'right';
  columns?: PyramidColumn<Data>[];
  aggregated?: (row: Row<Data>) => JSX.Element;
  aggregatedCell?: (props: CellContext<Data, Data>) => JSX.Element;
  aggregationFn?: AggregationFnOption<Data>;
  enableGrouping?: boolean;
  getGroupingValue?: (row: Row<Data>) => any;
  isColumnLocalized?: boolean;
}

interface TooltipProps {
  multiplier: number;
  region?: string;
  delay: number;
  category: string;
}

function TooltipContent({ multiplier, region, delay, category }: TooltipProps) {
  return (
    <div className="alpha-pyramids-tooltip">
      <div>
        {region}/D{delay}/{category}
      </div>
      <div>
        <FormattedMessageHelper id="genius.pyramidMultipliers.tooltip.multiplier" values={{ multiplier }} />
      </div>
      <div className="alpha-pyramids-tooltip__hint">
        <FormattedMessageHelper id="genius.pyramidMultipliers.tooltip.hint" />
      </div>
    </div>
  );
}

function getCellModifierByMultiplier(multiplier: number) {
  if (multiplier <= 1) {
    return 'color-0';
  }
  const index = Math.min(Math.ceil((multiplier - 1) / 0.2), 5);
  return `color-${index}`;
}

const getCommonPinningStyles = (column: Column<any>): CSSProperties => {
  const isPinned = column.getIsPinned();

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    zIndex: isPinned ? 1 : 0,
  };
};

export function getReactTableColumns(
  regionDelays: DelayByRegion[],
  defaultUniversesByRegion: Record<string, string>,
  openInSameTab: boolean,
  onCellClick?: (scope: string) => void
): PyramidColumn<PyramidMultiplier>[] {
  // @ts-ignore
  return (
    [
      {
        id: 'dataCategory',
        name: 'genius.pyramidMultipliers.category',
        accessor: (pyramid: PyramidMultiplier) => pyramid.category.id,
        active: true,
        display: true,
        type: 'choices',
        size: 120,
        cell: cellContext => {
          if (cellContext.row.original?.category?.name) {
            return (
              <div className={addModifiers('alphas-list-table__cell-content', ['with-wrapping'])}>
                {cellContext.row.original.category.name}
              </div>
            );
          }

          return <span />;
        },
      },
    ] as PyramidColumn<PyramidMultiplier>[]
  ).concat(
    regionDelays.map(delaysByRegion => ({
      id: delaysByRegion.region,
      name: delaysByRegion.region,
      isHeaderLocalized: false,
      accessor: (pyramid: PyramidMultiplier) => pyramid.regions,
      active: true,
      display: true,
      type: 'choices',
      isColumnLocalized: false,
      columns: delaysByRegion.delay.map(delay => ({
        id: `${delaysByRegion.region}_${delay}`,
        name: delay.toString(),
        isHeaderLocalized: false,
        accessor: (pyramid: PyramidMultiplier) => pyramid.regions[`${delaysByRegion.region}_${delay}`]?.pyramidMultiplier,
        active: true,
        display: true,
        type: 'choices',
        size: 16,

        cell: cellContext => {
          const stats = cellContext.row.original.regions[`${delaysByRegion.region}_${delay}`];
          if (stats) {
            return (
              <Popup
                content={
                  <TooltipContent
                    region={delaysByRegion.region}
                    delay={delay}
                    category={cellContext.row.original.category.name}
                    multiplier={stats.pyramidMultiplier}
                  />
                }
                on={'hover'}
                trigger={
                  <a
                    className="alpha-pyramids-cell__link"
                    target={openInSameTab ? '_self' : '_blank'}
                    rel="noreferrer"
                    onClick={() => onCellClick?.(`${delaysByRegion.region}/D${delay}/${cellContext.row.original.category.id}`)}
                    href={getDatasetsPath({
                      queryParams: {
                        region: delaysByRegion.region,
                        delay: delay,
                        category: cellContext.row.original.category.id,
                        universe: defaultUniversesByRegion[delaysByRegion.region],
                      },
                    })}
                  >
                    <div className={addModifiers('pyramid-multiplier-cell', [getCellModifierByMultiplier(stats.pyramidMultiplier)])}>
                      {stats.pyramidMultiplier}
                    </div>
                  </a>
                }
              />
            );
          }

          return <div className={addModifiers('alpha-pyramids-cell', ['disabled'])}></div>;
        },
      })),
    })) as PyramidColumn<PyramidMultiplier>[]
  );
}

interface PyramidMultipliersTableProps {
  openInSameTab?: boolean;
}

export const PyramidMultipliersTable = ({ openInSameTab = false, intl }: PyramidMultipliersTableProps & InjectedIntlProps) => {
  const { data, isLoading } = useAlphaPyramidMultipliers();
  const handleOrderChange = () => {};
  const onCellClick = useCallback(
    (category: string) => analytics.trackCustomEvent('genius_click_pyramid_multiplier', { event_label: category }),
    []
  );
  const transformColumn = React.useMemo(
    () =>
      getColumnTransform<PyramidMultiplier>({
        intl,
        sortOrder: { key: 'dataCategory', isAscending: false },
        handleOrderChange,
        isOrderDisabled: true,
      }),
    [intl]
  );

  const defaultUniversesByRegion = useDefaultUniverses();

  const tableColumns = useMemo(() => {
    if (!data) {
      return [];
    }
    return getReactTableColumns(data.regions, defaultUniversesByRegion, openInSameTab, onCellClick).map(transformColumn);
  }, [data, defaultUniversesByRegion, openInSameTab, onCellClick, transformColumn]);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: ['dataCategory'],
    right: [],
  });

  const table = useReactTable({
    columns: tableColumns as ColumnDef<PyramidMultiplier, any>[],
    data: (data && data.data) || [],
    state: {
      columnPinning,
    },
    onColumnPinningChange: setColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
    enableSorting: false,
  });

  const headerGroups = table.getHeaderGroups();

  // show loader when data load for the first time
  if (isLoading) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="agreements__content full-height">
        <div className="agreements__content-scroll full-height">
          <div className="agreements__not-available full-height">
            <FormattedMessageHelper id="genius.pyramidMultipliers.error" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="alpha-pyramids">
      <div className="agreements__content full-height">
        <div className="block-table-container">
          <div className="ReactTable block-table-wrapper -highlight">
            <div className="rt-table block-table" role="grid">
              {headerGroups.map((headerGroup, i) => (
                <div key={headerGroup.id} className={headerGroups.length === 2 && i === 0 ? 'rt-thead -headerGroups' : 'rt-thead -header'}>
                  <div
                    {...{
                      key: headerGroup.id,
                      className: 'rt-tr',
                      role: 'row',
                    }}
                  >
                    {headerGroup.headers.map(header => (
                      // eslint-disable-next-line react/jsx-key
                      <div
                        {...{
                          key: header.id,
                          className: getHeaderClassName<PyramidMultiplier>(header),
                          onClick: header.column.getToggleSortingHandler(),
                          role: 'columnheader',
                          style: {
                            width: header.getSize(),
                            flex: `${header.getSize()} 0 auto`,
                            background: 'inherit',
                            ...getCommonPinningStyles(header.column),
                          },
                        }}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div
                {...{
                  className: 'rt-tbody',
                }}
              >
                {table.getRowModel().rows.map((row, i) => (
                  <div className="rt-tr-group" role="rowgroup" key={row.id}>
                    <div
                      {...{
                        key: row.id,
                        className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`,
                        role: 'row',
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        // eslint-disable-next-line react/jsx-key
                        <div
                          {...{
                            key: cell.id,
                            // @ts-ignore
                            className: `${addModifiers('rt-td', [cell.column.id])} ${cell.column.columnDef.meta?.className ?? ''}`,
                            role: 'gridcell',
                            style: {
                              flex: `${cell.column.getSize()} 0 auto`,
                              width: cell.column.getSize(),
                              ...getCommonPinningStyles(cell.column),
                            },
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {getFooterGroups(table)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
